/**
 * Tool confirmation: edit a campaign's ICP filters.
 *
 * Input shape: {
 *   campaignId: string,
 *   campaignName?: string,
 *   filters: PipelineFilters (sicCodes, postTowns, counties, roleKeywords, companySizeRanges, ...)
 * }
 *
 * Renders the proposed filters as an editable JSON textarea. Kept deliberately
 * simple — a proper filter-builder is post-MVP (the agent produces the JSON
 * and users tweak fields manually).
 */

import { useState } from 'react';
import { FunnelIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/button';
import type { ToolConfirmationProps } from '../tool-registry';

export function EditCampaignICPTool({ input, onConfirm, onReject }: ToolConfirmationProps) {
  const campaignName = (input.campaignName as string) || 'this campaign';
  const [filtersJson, setFiltersJson] = useState(() =>
    JSON.stringify(input.filters ?? {}, null, 2),
  );
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = () => {
    try {
      const filters = JSON.parse(filtersJson);
      setError(null);
      onConfirm({ ...input, filters });
    } catch (err) {
      setError(`Invalid JSON: ${(err as Error).message}`);
    }
  };

  return (
    <div className="h-full flex items-center justify-center p-8">
      <div className="bg-card border border-border rounded-2xl p-6 max-w-xl w-full shadow-lg">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-warning-surface flex items-center justify-center">
            <FunnelIcon className="h-5 w-5 text-warning-foreground" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Update campaign ICP</h2>
            <p className="text-xs text-muted-foreground">{campaignName}</p>
          </div>
        </div>

        <p className="text-xs text-muted-foreground mb-3">
          Review the agent's proposed filters. Edit the JSON below if needed before applying.
          Changes take effect on the next prospecting cycle.
        </p>

        <textarea
          value={filtersJson}
          onChange={(e) => { setFiltersJson(e.target.value); setError(null); }}
          rows={12}
          spellCheck={false}
          className="w-full font-mono text-xs p-3 rounded-lg border border-control-border bg-input-bg"
        />

        {error && (
          <p className="text-xs text-danger-foreground mt-2">{error}</p>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="secondary" onClick={onReject}>Cancel</Button>
          <Button variant="primary" onClick={handleConfirm}>Apply ICP</Button>
        </div>
      </div>
    </div>
  );
}
